import {
  getConfigByCountry,
  getCurrencySymbolPosition,
  getCurrencySpaceBetweenSymbol,
} from './config';
import { deflateServerNumber, parseServerNumber } from './convertNumber';
import * as FormatCurrency from './formatCurrency';
import * as CompactNumber from './compactNumber';
import * as FormatNumber from './formatNumber';
import {
  CountryCode,
  ParsableNumber,
  Precision,
  Notch,
  LanguageCode,
  CountryConfig,
} from './typings';
import { FormatCurrencyNumberOptions } from './formatCurrency';
import { CurrencyConfig } from './config/currencyConfig';

export * from './constants';
export * from './config';
export * from './convertNumber';
export * from './formatNumber';
export * from './formatCurrency';
export * from './compactNumber';
export * from './typings';
export { getNumberParts } from './utils';

/**
 * Saving effort for getting all country binded utils. Avoiding writing binded functions one by one in each project.
 * @param {string} country
 * @param {LanguageCode?} language Current language
 * @return {Object.<Function>}
 *
 * @prop {module:config.getCurrencyConfig} getCurrencyConfig `getCurrencyConfig() => CurrencyConfig`
 * @prop {module:config.getCountryConfig} getCountryConfig getCountryConfig() => CountryConfig
 * @prop {module:convertNumber.deflateServerNumber} deflateServerNumber deflateServerNumber(input: number&#124;string) => number
 * @prop {module:convertNumber.parseServerNumber} parseServerNumber parseServerNumber(input: number&#124;string) => number
 * @prop {module:config.getCurrencySymbol} getCurrencySymbol getCurrencySymbol() => string
 * @prop {module:config.getNotches} getNotches getNotches() => Notch[]
 * @prop {module:formatNumber.formatNumber} formatServerNumber formatServerNumber(input: number&#124;string, config?: number&#124;false&#124;CurrencyConfig) => string
 * @prop {module:formatCurrency.formatCurrencyNumber} formatCurrencyNumber DEPRECATED, pls use formatCurrency instead. formatCurrencyNumber(input: number&#124;string, options?: number&#124;false&#124;FormatCurrencyNumberOptions) => string
 * @prop {module:formatCurrency.formatCurrencyNumber} formatServerCurrencyNumber DEPRECATED, pls use formatServerCurrency instead. formatServerCurrencyNumber(input: number&#124;string, options?: number&#124;false&#124;FormatCurrencyNumberOptions) => string
 * @prop {module:formatCurrency.formatCurrency} formatCurrency formatCurrency(input: number&#124;string, options?: number&#124;false&#124;FormatCurrencyNumberOptions) => string
 * @prop {module:formatCurrency.formatCurrency} formatServerCurrency formatServerCurrency(input: number&#124;string, options?: number&#124;false&#124;FormatCurrencyNumberOptions) => string
 * @prop {module:compactNumber.compactNumber} compactServerNumber compactServerNumber(input: number&#124;string, maxPrecision?: number&#124;false, notches?: Notch[]) => string
 * @prop {module:compactNumber.localizeCompactNumber} localizeCompactNumber DEPRECATED, pls use compactCurrency instead. localizeCompactNumber(input: number&#124;string, options?: number&#124;false&#124;FormatCurrencyNumberOptions) => string
 * @prop {module:compactNumber.localizeCompactNumber} localizeCompactServerNumber DEPRECATED, pls use compactServerCurrency instead. localizeCompactServerNumber(input: number&#124;string, options?: number&#124;false&#124;FormatCurrencyNumberOptions) => string
 * @prop {module:compactNumber.compactCurrency} compactCurrency compactCurrency(input: number&#124;string, options?: number&#124;false&#124;FormatCurrencyNumberOptions) => string
 * @prop {module:compactNumber.compactCurrency} compactServerCurrency compactServerCurrency(input: number&#124;string, options?: number&#124;false&#124;FormatCurrencyNumberOptions) => string
 */

export function getUtilsByCountry(
  country: CountryCode,
  // TODO: make `language` a required parameter in next major version
  language?: LanguageCode
) {
  const Config = getConfigByCountry(country, language);
  return {
    getCurrencyConfig(): CurrencyConfig {
      return Config.getCurrencyConfig(country);
    },
    getCountryConfig(): CountryConfig {
      return Config.getCountryConfig(country, language);
    },
    deflateServerNumber(input: ParsableNumber): ParsableNumber {
      return deflateServerNumber(input, country);
    },
    parseServerNumber(input: ParsableNumber): ParsableNumber {
      return parseServerNumber(input, country);
    },
    getCurrencySymbol(): string | Readonly<[string, string]> {
      return Config.getCurrencySymbol(country);
    },
    getCurrencySymbolPosition(): string {
      return getCurrencySymbolPosition(country);
    },
    getCurrencySpaceBetweenSymbol(): boolean | Readonly<[boolean, boolean]> {
      return getCurrencySpaceBetweenSymbol(country);
    },
    getNotches(): Notch[] {
      return Config.getNotches(country, language);
    },
    formatServerNumber(
      input: ParsableNumber,
      options?: Precision | Partial<CurrencyConfig>
    ): ParsableNumber {
      return FormatNumber.formatNumber(
        deflateServerNumber(input, country),
        options
      );
    },
    formatCurrencyNumber(
      input: ParsableNumber,
      options?: FormatCurrencyNumberOptions
    ): ParsableNumber {
      return FormatCurrency.formatCurrencyNumber(country, input, options);
    },
    formatServerCurrencyNumber(
      input: ParsableNumber,
      options?: FormatCurrencyNumberOptions
    ) {
      return FormatCurrency.formatCurrencyNumber(
        country,
        deflateServerNumber(input, country),
        options
      );
    },
    formatCurrency(
      input: number,
      options?: FormatCurrencyNumberOptions
    ): string {
      return FormatCurrency.formatCurrency(country, input, options);
    },
    formatServerCurrency(
      input: ParsableNumber,
      options?: FormatCurrencyNumberOptions
    ): string {
      return FormatCurrency.formatCurrency(
        country,
        deflateServerNumber(input, country),
        options
      );
    },
    compactServerNumber(
      input: ParsableNumber,
      maxPrecision?: Precision,
      notches?: Notch[]
    ): string {
      return CompactNumber.compactNumber(
        deflateServerNumber(input, country),
        maxPrecision,
        notches
      );
    },
    localizeCompactNumber(
      input: ParsableNumber,
      options?: FormatCurrencyNumberOptions
    ): string {
      return CompactNumber.localizeCompactNumber(
        country,
        input,
        options,
        language
      );
    },
    localizeCompactServerNumber(
      input: ParsableNumber,
      options?: FormatCurrencyNumberOptions
    ): string {
      return CompactNumber.localizeCompactNumber(
        country,
        deflateServerNumber(input, country),
        options,
        language
      );
    },
    compactCurrency(
      input: ParsableNumber,
      options?: FormatCurrencyNumberOptions,
      notches?: Notch[]
    ): string {
      return CompactNumber.compactCurrency(
        country,
        input,
        options,
        notches,
        language
      );
    },
    compactServerCurrency(
      input: ParsableNumber,
      options?: FormatCurrencyNumberOptions,
      notches?: Notch[]
    ): string {
      return CompactNumber.compactCurrency(
        country,
        deflateServerNumber(input, country),
        options,
        notches,
        language
      );
    },
  };
}

export default getUtilsByCountry;

/**
 * @typedef {Object} CurrencyConfig
 * @prop {string} symbol 							Exp., '$'.
 * @prop {number} [precision]						How many digits of decimal number should exist in formated number. It will fill with 0 if formated number do not have enough digits of decimal number.
 * @prop {number} [maxPrecision]				How many digits of decimal number allow to exist in formated number. It will not fill 0 to the number unless you set precision.
 * @prop {string|Function} [separator] 	Exp., ','. Used in formatting currency, like '$12,345.00'.
 * @prop {string} [decimalPoint]				For converting data retreived from server to correct number. So the number passed in formatter will be `number x convertRate`. Since 1.1.2.
 */

/**
 * Notch is the name of option for compact number levels.
 * @typedef {Object} Notch
 * @prop {number} minValue The minimum value that a number should meet to be able to use the unit defined.
 * @prop {string} unit Unit should be a string like 'k', 'm', 'b'.
 */

/**
 * CountryCurrencyConfig is a configuration for country with multiple currencies, to help converting to each other.
 * @typedef {Object} CounryCurrencyConfig
 * @prop {number} [convertRate] 	Default to be 1 if this prop is not given.
 */

/**
 * CountryConfig is the localized configuration for each country that defined by local.
 * @typedef {Object} CountryConfig
 * @prop {Object.<CounryCurrencyConfig>} currencies
 * @prop {module:constants.CURRENCY_ID} defaultCurrency
 * @prop {module:constants.COUNTRY_CODE} [serverCurrency]
 * @prop {Notch[]} notches
 */
